import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { FileSearch, Factory, PackageOpen, Pencil, Scale, Search, Snowflake, Tag, Thermometer, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Card, Button, Spinner, DateInput, ConfirmDeleteDialog, inputCls } from "../../components/ui-kit";
import { formatItalianDate, formatItalianDateTime } from "../../lib/dateUtils";
import { romeDateKey } from "../../lib/dayRollover";
import AcademyTitleBadge from "../../academy/AcademyTitleBadge";
import MocaProductionDialog from "../../components/MocaProductionDialog";
import { mocaLabelText } from "../../lib/moca";
import PostCookingChecksDialog, { POST_COOKING_CHECK_KINDS } from "../../components/PostCookingChecksDialog";
import { POST_COOKING_LABELS } from "../../lib/postCookingModes";

const searchable = (value) => (value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

const EXPIRY_DOT = { ok: "bg-emerald-500", soon: "bg-amber-500", expired: "bg-red-500", unknown: "bg-slate-300" };

function expiryStatus(expiryDate, today, tomorrow) {
  if (!expiryDate) return "unknown";
  if (expiryDate < today) return "expired";
  if (expiryDate === today || expiryDate === tomorrow) return "soon";
  return "ok";
}

function expiryNote(expiryDate, status, today, tomorrow) {
  if (!expiryDate) return "Scadenza non disponibile";
  const formatted = formatItalianDate(expiryDate);
  if (status === "expired") return `Scaduta il ${formatted}`;
  if (status === "soon") return expiryDate === today ? "Scade oggi" : "Scade domani";
  return `Scade il ${formatted}`;
}

export default function Produzioni() {
  const nav = useNavigate();
  const { ctx, isLocked } = useOutletContext();
  const mocaOn = !isLocked("moca");
  const [mocaFor, setMocaFor] = useState(null);
  const [checksFor, setChecksFor] = useState(null);
  const [prods, setProds] = useState(null);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  // Stessi ruoli dell'endpoint DELETE /app/productions/{id}.
  const canDelete = ["titolare", "responsabile", "super_admin"].includes(ctx.user?.role);
  // Stessi ruoli dell'endpoint PATCH /app/productions/{id}/quantities.
  const canEditQuantities = ["titolare", "responsabile", "addetto", "super_admin"].includes(ctx.user?.role);
  const saveQuantities = (id, values) => setProds((current) => current.map((p) => p.id === id ? { ...p, ...values } : p));
  const load = useCallback(() => api.get("/app/productions").then(({ data }) => setProds(data.productions)).catch(() => setProds([])), []);
  useEffect(() => { load(); }, [load]);
  // Sonde termometriche registrate in Attrezzature: scelta facoltativa.
  const [probes, setProbes] = useState([]);
  useEffect(() => { api.get("/app/equipment").then(({ data }) => setProbes((data.equipment || []).filter((item) => item.type === "sonda_termometrica"))).catch(() => setProbes([])); }, []);
  const siteName = useMemo(() => Object.fromEntries((ctx.sites || []).map((site) => [site.id, site.name])), [ctx.sites]);

  const today = romeDateKey();
  const tomorrow = romeDateKey(new Date(Date.now() + 24 * 60 * 60 * 1000));

  const filtered = useMemo(() => (prods || []).filter((p) =>
    (!search || searchable(p.recipe_name).includes(searchable(search)) || searchable(p.lot_code).includes(searchable(search))) &&
    (!dateFilter || p.day_key === dateFilter)
  ), [prods, search, dateFilter]);
  const filtersActive = search || dateFilter;
  const clearFilters = () => { setSearch(""); setDateFilter(""); };
  // needs_blast: cotto e non destinato al consumo immediato (produzioni
  // precedenti al campo: vale is_cooked, come prima).
  const needsBlast = (p) => (p.needs_blast ?? p.is_cooked) && !p.blast_count;
  const confirmDelete = async () => {
    setDeleteBusy(true);
    try {
      await api.delete(`/app/productions/${deleting.id}`);
      toast.success(`Produzione ${deleting.lot_code} eliminata`);
      setDeleting(null);
      load();
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail)); }
    finally { setDeleteBusy(false); }
  };
  const pendingBlastCount = useMemo(() => (prods || []).filter((p) => p.blast_pending_count > 0 || needsBlast(p)).length, [prods]);

  return <div className="space-y-6" data-testid="produzioni-page">
    <div><p className="text-xs uppercase tracking-[0.18em] text-slate-400">Produzione</p><div className="flex flex-wrap items-center gap-2"><h1 className="font-heading text-3xl font-bold">Produzioni</h1><AcademyTitleBadge /></div><p className="mt-1 text-sm text-slate-500">Storico delle produzioni registrate, con lotto, scadenza, etichetta e QR.</p></div>

    {pendingBlastCount > 0 && (
      <Card className="border-2 border-violet-300 bg-violet-50 p-4" role="alert" data-testid="produzioni-blast-reminder">
        <div className="flex items-center gap-3">
          <Snowflake className="h-6 w-6 shrink-0 text-violet-600" />
          <div className="text-sm text-violet-900">
            <strong>{pendingBlastCount} {pendingBlastCount === 1 ? "abbattimento" : "abbattimenti"} da completare</strong> — per i prodotti cotti destinati alla conservazione refrigerata o congelata, e per quelli non cotti abbattuti e congelati, va registrata la fase di raffreddamento/abbattimento prevista dal piano HACCP, indipendentemente da come è stata creata la produzione. Il pulsante viola lampeggiante sulla scheda indica quali.
          </div>
        </div>
      </Card>
    )}

    {!prods ? <Spinner /> : prods.length === 0 ? (
      <Card className="p-10 text-center text-slate-400"><Factory className="mx-auto mb-3 h-10 w-10 opacity-40" />Nessuna produzione registrata.</Card>
    ) : (
      <>
        <Card className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca produzione per ricetta o lotto…"
                aria-label="Cerca produzione"
                className={`${inputCls} pl-10 pr-10`}
                data-testid="produzioni-search"
              />
              {search && (
                <button type="button" onClick={() => setSearch("")} className="absolute right-2 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-navy" aria-label="Cancella ricerca" data-testid="produzioni-search-clear">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="sm:w-52 sm:shrink-0">
              <DateInput valueMode="iso" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} aria-label="Filtra per data di produzione" data-testid="produzioni-filter-date" />
            </div>
          </div>
          {filtersActive && (
            <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-500">
              <span>{filtered.length} {filtered.length === 1 ? "produzione trovata" : "produzioni trovate"} su {prods.length}</span>
              <button type="button" onClick={clearFilters} className="font-medium text-navy underline underline-offset-4">Azzera filtri</button>
            </div>
          )}
        </Card>

        {filtered.length === 0 ? (
          <Card className="p-10 text-center text-slate-400"><Search className="w-10 h-10 mx-auto mb-3 opacity-40" />Nessuna produzione corrisponde ai filtri.</Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {filtered.map((p) => {
              const status = expiryStatus(p.expiry_date, today, tomorrow);
              return (
                <Card key={p.id} className="p-5" data-testid={`produzione-${p.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h2 className="font-heading font-semibold text-navy break-words">{p.recipe_name?.toUpperCase()}</h2>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-slate-400">
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-gold">{p.lot_code}</span>
                        <span>{p.automatic ? "Automatica" : "Registrata"}</span>
                        <button type="button" onClick={() => nav(`/app/tracciabilita?q=${encodeURIComponent(p.lot_code)}`)} className="inline-flex min-h-[32px] items-center gap-1 font-medium text-navy underline" data-testid={`produzione-ddt-${p.id}`}><FileSearch className="h-3.5 w-3.5" /> DDT materie prime</button>
                      </div>
                    </div>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => setDeleting(p)}
                        disabled={!!deleting}
                        className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        aria-label={`Elimina la produzione ${p.recipe_name} lotto ${p.lot_code}`}
                        title="Elimina produzione"
                        data-testid={`produzione-delete-${p.id}`}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    )}
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg border border-hairline bg-slate-50 p-3">
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Sede</div><div className="mt-0.5 break-words text-xs font-medium text-navy">{siteName[p.site_id] || "—"}</div></div>
                    <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Prodotta il</div><div className="mt-0.5 text-xs font-medium text-navy">{formatItalianDate(p.created_at)} · {p.operator_name || "—"}</div></div>
                  </div>

                  <ProductionQuantities production={p} canEdit={canEditQuantities} onSaved={saveQuantities} />

                  {p.is_cooked && <ProductionCoreTemp production={p} probes={probes.filter((probe) => probe.site_id === p.site_id)} canEdit={canEditQuantities} onSaved={saveQuantities} />}

                  <div className="mt-2 flex items-center gap-2 rounded-lg border border-hairline p-3" data-testid={`produzione-expiry-${p.id}`}>
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${EXPIRY_DOT[status]}`} aria-hidden="true" />
                    <span className={`text-sm font-medium ${status === "expired" ? "text-red-700" : status === "soon" ? "text-amber-700" : status === "ok" ? "text-emerald-700" : "text-slate-500"}`}>
                      {expiryNote(p.expiry_date, status, today, tomorrow)}
                    </span>
                  </div>

                  {p.blast_pending_count > 0 ? (
                    <Button
                      variant="danger"
                      className="mt-2 w-full animate-pulse !bg-violet-600 ring-2 ring-violet-300 ring-offset-1 hover:!bg-violet-700"
                      onClick={() => nav(`/app/abbattimento?confirm=${p.blast_pending_id}`)}
                      data-testid={`produzione-blast-${p.id}`}
                    >
                      <Snowflake className="h-4 w-4" /> Conferma abbattimento
                    </Button>
                  ) : needsBlast(p) ? (
                    <Button
                      variant="danger"
                      className="mt-2 w-full animate-pulse !bg-violet-600 ring-2 ring-violet-300 ring-offset-1 hover:!bg-violet-700"
                      onClick={() => nav(`/app/abbattimento?production_id=${p.id}`)}
                      data-testid={`produzione-blast-${p.id}`}
                    >
                      <Snowflake className="h-4 w-4" /> Registra abbattimento
                    </Button>
                  ) : (
                    <div className="mt-2 flex items-center gap-2 rounded-lg border border-hairline p-3" data-testid={`produzione-blast-${p.id}`}>
                      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${p.blast_count > 0 ? "bg-sky-500" : "bg-slate-300"}`} aria-hidden="true" />
                      <span className={`text-sm font-medium ${p.blast_count > 0 ? "text-sky-700" : "text-slate-500"}`}>
                        {p.blast_count > 0 ? `Abbattuta (${p.blast_count} ${p.blast_count === 1 ? "ciclo" : "cicli"})` : p.needs_blast === false && p.is_cooked && p.post_cooking_destination ? "Abbattimento non previsto" : "Non abbattuta"}
                      </span>
                    </div>
                  )}

                  {p.is_cooked && POST_COOKING_CHECK_KINDS[p.post_cooking_destination] && (
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-hairline p-3" data-testid={`produzione-post-cooking-${p.id}`}>
                      <div className="min-w-0 text-sm">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Dopo la cottura</div>
                        <div className="break-words text-xs font-medium text-navy">{POST_COOKING_LABELS[p.post_cooking_destination]}</div>
                      </div>
                      <button type="button" onClick={() => setChecksFor(p)} className="min-h-[40px] shrink-0 rounded-lg border border-navy/30 px-3 text-xs font-semibold text-navy hover:bg-slate-50" data-testid={`produzione-post-cooking-open-${p.id}`}>Controlli</button>
                    </div>
                  )}

                  {mocaOn && (
                    <div className={`mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 ${p.moca_lots?.length ? "border-hairline" : "border-sky-200 bg-sky-50/50"}`} data-testid={`produzione-moca-${p.id}`}>
                      <div className="min-w-0 text-sm">
                        <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400"><PackageOpen className="h-3.5 w-3.5" /> Imballaggi MOCA</div>
                        {p.moca_lots?.length ? p.moca_lots.map((m) => <div key={m.lot_id} className="break-words text-xs font-medium text-navy">{mocaLabelText(m)}</div>)
                          : <div className="text-xs text-slate-500">Nessun MOCA associato</div>}
                      </div>
                      <button type="button" onClick={() => setMocaFor(p.id)} className="min-h-[40px] shrink-0 rounded-lg border border-navy/30 px-3 text-xs font-semibold text-navy hover:bg-slate-50" data-testid={`produzione-moca-edit-${p.id}`}>{p.moca_lots?.length ? "Modifica" : "Associa MOCA"}</button>
                    </div>
                  )}

                  <Button variant="outline" className="mt-2 w-full" onClick={() => nav(`/app/etichette?prod=${p.id}`)} data-testid="produzione-label-link"><Tag className="h-4 w-4" /> Etichetta & QR</Button>
                </Card>
              );
            })}
          </div>
        )}
      </>
    )}

    {checksFor && <PostCookingChecksDialog production={checksFor} onClose={() => setChecksFor(null)} />}
    {mocaFor && <MocaProductionDialog productionId={mocaFor} onClose={() => setMocaFor(null)} onSaved={(lots) => { setProds((cur) => cur.map((p) => p.id === mocaFor ? { ...p, moca_lots: lots } : p)); setMocaFor(null); }} />}

    <ConfirmDeleteDialog
      open={!!deleting} busy={deleteBusy}
      title="Eliminare questa produzione?"
      description={deleting ? `La produzione "${deleting.recipe_name?.toUpperCase()}" (lotto ${deleting.lot_code}, del ${formatItalianDate(deleting.created_at)}) verrà eliminata definitivamente, insieme ai suoi abbattimenti, al QR pubblico del lotto e ai report collegati. Le etichette già stampate con questo QR non funzioneranno più. Questa operazione non può essere annullata.` : ""}
      onCancel={() => setDeleting(null)} onConfirm={confirmDelete}
    />
  </div>;
}

const formatGrams = (value) => `${Number(value).toLocaleString("it-IT", { maximumFractionDigits: 1 })} g`;

// Pezzi e peso totale: facoltativi, indicati in creazione oppure aggiunti
// dopo (spesso il peso si conosce solo a fine lavorazione).
function ProductionQuantities({ production: p, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [pieces, setPieces] = useState("");
  const [weight, setWeight] = useState("");
  const [busy, setBusy] = useState(false);
  const hasValues = p.pieces || p.weight_g;
  const start = () => { setPieces(p.pieces ?? ""); setWeight(p.weight_g ?? ""); setEditing(true); };
  const parse = (value, fn) => { const n = fn(String(value).replace(",", ".")); return Number.isFinite(n) && n > 0 ? n : null; };
  const save = async () => {
    const values = { pieces: parse(pieces, (v) => parseInt(v, 10)), weight_g: parse(weight, parseFloat) };
    setBusy(true);
    try {
      await api.patch(`/app/productions/${p.id}/quantities`, values);
      onSaved(p.id, values);
      setEditing(false);
      toast.success("Pezzi e peso aggiornati");
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div className="mt-2 rounded-lg border border-hairline p-3" data-testid={`produzione-quantities-edit-${p.id}`}>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs font-medium text-slate-600">Pezzi<input type="number" inputMode="numeric" min="1" step="1" value={pieces} onChange={(event) => setPieces(event.target.value)} placeholder="Es. 12" className={`${inputCls} mt-1 block w-28`} data-testid={`produzione-pieces-${p.id}`} /></label>
          <label className="text-xs font-medium text-slate-600">Peso totale (g)<input type="number" inputMode="decimal" min="1" step="1" value={weight} onChange={(event) => setWeight(event.target.value)} placeholder="Es. 1500" className={`${inputCls} mt-1 block w-32`} data-testid={`produzione-weight-${p.id}`} /></label>
          <div className="flex gap-2">
            <Button variant="success" loading={busy} onClick={save} data-testid={`produzione-quantities-save-${p.id}`}>Salva</Button>
            <Button variant="outline" disabled={busy} onClick={() => setEditing(false)}>Annulla</Button>
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-500">Campi facoltativi: lascia vuoto ciò che non vuoi indicare.</p>
      </div>
    );
  }
  return (
    <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-hairline p-3" data-testid={`produzione-quantities-${p.id}`}>
      <span className={`inline-flex items-center gap-2 text-sm ${hasValues ? "font-medium text-navy" : "text-slate-400"}`}>
        <Scale className="h-4 w-4 shrink-0" aria-hidden="true" />
        {hasValues ? [p.pieces && `${p.pieces} ${p.pieces === 1 ? "pezzo" : "pezzi"}`, p.weight_g && formatGrams(p.weight_g)].filter(Boolean).join(" · ") : "Pezzi e peso non indicati"}
      </span>
      {canEdit && (
        <button type="button" onClick={start} className="inline-flex min-h-[44px] items-center gap-1 px-2 text-xs font-medium text-navy underline" data-testid={`produzione-quantities-open-${p.id}`}>
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> {hasValues ? "Modifica" : "Aggiungi"}
        </button>
      )}
    </div>
  );
}

// Temperatura al cuore misurata con la sonda: facoltativa, non blocca nulla.
// Sotto l'obiettivo della ricetta mostra solo un avviso.
function ProductionCoreTemp({ production: p, probes, canEdit, onSaved }) {
  const check = p.core_temp_check;
  const target = check?.target ?? p.core_temp_target;
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [probeId, setProbeId] = useState("");
  const [busy, setBusy] = useState(false);
  const start = () => { setValue(check?.value ?? ""); setProbeId(check?.probe_id || (probes.length === 1 ? probes[0].id : "")); setEditing(true); };
  const save = async () => {
    const n = parseFloat(String(value).replace(",", "."));
    const body = { value: Number.isFinite(n) ? n : null, probe_id: probeId || null };
    setBusy(true);
    try {
      const { data } = await api.patch(`/app/productions/${p.id}/core-temp`, body);
      onSaved(p.id, { core_temp_check: data.core_temp_check, core_temp: data.core_temp });
      setEditing(false);
      toast.success(body.value == null ? "Temperatura al cuore rimossa" : "Temperatura al cuore registrata");
    } catch (error) { toast.error(formatApiErrorDetail(error.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div className="mt-2 rounded-lg border border-hairline p-3" data-testid={`produzione-coretemp-edit-${p.id}`}>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs font-medium text-slate-600">Temperatura al cuore (°C)<input type="number" inputMode="decimal" step="0.1" value={value} onChange={(event) => setValue(event.target.value)} placeholder={target != null ? `Obiettivo ${target}` : "Es. 78"} className={`${inputCls} mt-1 block w-32`} data-testid={`produzione-coretemp-value-${p.id}`} /></label>
          <label className="text-xs font-medium text-slate-600">Sonda utilizzata<select value={probeId} onChange={(event) => setProbeId(event.target.value)} className={`${inputCls} mt-1 block w-48`} data-testid={`produzione-coretemp-probe-${p.id}`}><option value="">Non indicata</option>{probes.map((probe) => <option key={probe.id} value={probe.id}>{probe.name}</option>)}</select></label>
          <div className="flex gap-2">
            <Button variant="success" loading={busy} onClick={save} data-testid={`produzione-coretemp-save-${p.id}`}>Salva</Button>
            <Button variant="outline" disabled={busy} onClick={() => setEditing(false)}>Annulla</Button>
          </div>
        </div>
        <p className="mt-2 text-xs text-slate-500">Campi facoltativi: misura con la sonda nel punto più spesso del prodotto, a fine cottura. Lascia vuoto per non indicarla.{probes.length === 0 && " Le sonde si aggiungono in Attrezzature (tipologia «Sonda termometrica al cuore»)."}</p>
      </div>
    );
  }
  const below = check && check.reached === false;
  return (
    <div className={`mt-2 rounded-lg border p-3 ${below ? "border-amber-300 bg-amber-50" : "border-hairline"}`} data-testid={`produzione-coretemp-${p.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`inline-flex items-center gap-2 text-sm ${check ? (below ? "font-medium text-amber-800" : "font-medium text-navy") : "text-slate-400"}`}>
          <Thermometer className="h-4 w-4 shrink-0" aria-hidden="true" />
          {check
            ? <span>Al cuore {check.value} °C{target != null && <> · obiettivo {target} °C {check.reached ? "raggiunto" : "non raggiunto"}</>}</span>
            : <span>Temperatura al cuore: da completare <span className="text-xs">(facoltativa{target != null ? `, obiettivo ${target} °C` : ""})</span></span>}
        </span>
        {canEdit && (
          <button type="button" onClick={start} className="inline-flex min-h-[44px] shrink-0 items-center gap-1 px-2 text-xs font-medium text-navy underline" data-testid={`produzione-coretemp-open-${p.id}`}>
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> {check ? "Modifica" : "Aggiungi"}
          </button>
        )}
      </div>
      {check && <p className="mt-1 text-xs text-slate-500">{[check.probe_name && `Sonda: ${check.probe_name}`, check.operator_name, check.measured_at && formatItalianDateTime(check.measured_at)].filter(Boolean).join(" · ")}</p>}
      {below && <p className="mt-1 text-xs text-amber-800">Valuta di proseguire la cottura e rimisurare. Se il prodotto non viene ricotto, puoi registrare l’azione in Non conformità.</p>}
    </div>
  );
}
